import React, { useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { MemberAuthProvider } from './context/MemberAuthContext';

// Public Layout
import { Navbar } from './components/site/Navbar';
import { Footer } from './components/site/Footer';
import { WhatsAppButton } from './components/site/WhatsAppButton';
import { ThemeApplier } from './components/site/ThemeApplier';
import { SiteJsonLd } from './components/site/SiteJsonLd';
import { CanonicalManager } from './components/site/CanonicalManager';
import { AnalyticsLoader } from './components/site/AnalyticsLoader';
import { ErrorBoundary } from './components/site/ErrorBoundary';
import { CookieConsent } from './components/site/CookieConsent';
import NotFound from './pages/NotFound';
import { MenuGate } from './components/site/MenuGate';

// Public Pages
import Home from './pages/Home';
import Corporate from './pages/Corporate';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import ECatalog from './pages/ECatalog';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import Team from './pages/Team';
import FAQ from './pages/FAQ';
import Gallery from './pages/Gallery';
import Testimonials from './pages/Testimonials';
import Services from './pages/Services';
import CustomPage from './pages/CustomPage';
import LegalPage from './pages/LegalPage';
import ThemePreview from './pages/ThemePreview';

// Member (Üye) Pages
import MemberLogin from './pages/member/Login';
import MemberRegister from './pages/member/Register';
import MemberVerify from './pages/member/Verify';
import MemberForgotPassword from './pages/member/ForgotPassword';
import MemberResetPassword from './pages/member/ResetPassword';
import MemberProfile from './pages/member/Profile';
import PaymentResult from './pages/member/PaymentResult';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import Dashboard from './pages/admin/Dashboard';
import SliderManager from './pages/admin/SliderManager';
import ProjectManager from './pages/admin/ProjectManager';
import CatalogManager from './pages/admin/CatalogManager';
import CorporateEditor from './pages/admin/CorporateEditor';
import FooterSettings from './pages/admin/FooterSettings';
import ContactSettings from './pages/admin/ContactSettings';
import SEOSettings from './pages/admin/SEOSettings';
import MessagesManager from './pages/admin/MessagesManager';
import Profile from './pages/admin/Profile';
import UserManager from './pages/admin/UserManager';
import TenantManager from './pages/admin/TenantManager';
import BlogManager from './pages/admin/BlogManager';
import TestimonialManager from './pages/admin/TestimonialManager';
import TeamManager from './pages/admin/TeamManager';
import FAQManager from './pages/admin/FAQManager';
import GalleryManager from './pages/admin/GalleryManager';
import ThemeEditor from './pages/admin/ThemeEditor';
import MenuSettings from './pages/admin/MenuSettings';
import ServiceManager from './pages/admin/ServiceManager';
import CustomPageManager from './pages/admin/CustomPageManager';
import LegalPageManager from './pages/admin/LegalPageManager';
import MemberManager from './pages/admin/MemberManager';
import ProductManager from './pages/admin/ProductManager';
import PaymentManager from './pages/admin/PaymentManager';
import PaymentSettings from './pages/admin/PaymentSettings';
import NewsletterManager from './pages/admin/NewsletterManager';
import AnalyticsSettings from './pages/admin/AnalyticsSettings';

import './App.css';

function PublicLayout({ children, transparent = false }) {
  return (
    <div className="min-h-screen flex flex-col">
      <CanonicalManager />
      <SiteJsonLd />
      <Navbar transparent={transparent} />
      <main className="flex-1">{children}</main>
      <Footer />
      <WhatsAppButton />
      <CookieConsent />
    </div>
  );
}

function HomeWrapper() {
  const [transparentNav, setTransparentNav] = useState(false);

  const handleVersionDetected = useCallback((version) => {
    setTransparentNav(version === 'v2' || version === 'v4');
  }, []);

  return (
    <PublicLayout transparent={transparentNav}>
      <Home onVersionDetected={handleVersionDetected} />
    </PublicLayout>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
      <AuthProvider>
        <MemberAuthProvider>
          <Router>
            <Toaster position="top-right" richColors closeButton />
            <ThemeApplier />
            <AnalyticsLoader />
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomeWrapper />} />
            {/* Sections that can be switched off in Admin → Menü Ayarları show 404 when off */}
            <Route path="/kurumsal" element={<PublicLayout><MenuGate menuKey="kurumsal"><Corporate /></MenuGate></PublicLayout>} />
            <Route path="/projeler/:category" element={<PublicLayout><MenuGate menuKey="projeler"><Projects /></MenuGate></PublicLayout>} />
            <Route path="/proje/:slug" element={<PublicLayout><MenuGate menuKey="projeler"><ProjectDetail /></MenuGate></PublicLayout>} />
            <Route path="/e-katalog" element={<PublicLayout><MenuGate menuKey="ekatalog"><ECatalog /></MenuGate></PublicLayout>} />
            <Route path="/iletisim" element={<PublicLayout><MenuGate menuKey="iletisim"><Contact /></MenuGate></PublicLayout>} />
            <Route path="/blog" element={<PublicLayout><MenuGate menuKey="blog"><Blog /></MenuGate></PublicLayout>} />
            <Route path="/blog/:slug" element={<PublicLayout><MenuGate menuKey="blog"><Blog /></MenuGate></PublicLayout>} />
            <Route path="/ekip" element={<PublicLayout><MenuGate menuKey="ekip"><Team /></MenuGate></PublicLayout>} />
            <Route path="/sss" element={<PublicLayout><MenuGate menuKey="sss"><FAQ /></MenuGate></PublicLayout>} />
            <Route path="/galeri" element={<PublicLayout><MenuGate menuKey="galeri"><Gallery /></MenuGate></PublicLayout>} />
            <Route path="/referanslar" element={<PublicLayout><MenuGate menuKey="referanslar"><Testimonials /></MenuGate></PublicLayout>} />
            <Route path="/hizmetlerimiz" element={<PublicLayout><MenuGate menuKey="hizmetler"><Services /></MenuGate></PublicLayout>} />
            <Route path="/hizmet/:slug" element={<PublicLayout><MenuGate menuKey="hizmetler"><Services /></MenuGate></PublicLayout>} />
            <Route path="/sayfa/:slug" element={<PublicLayout><CustomPage /></PublicLayout>} />
            <Route path="/yasal/:slug" element={<PublicLayout><LegalPage /></PublicLayout>} />
            <Route path="/tema-onizleme" element={<ThemePreview />} />

            {/* Member (Üye) Routes */}
            <Route path="/uye/giris" element={<PublicLayout><MemberLogin /></PublicLayout>} />
            <Route path="/uye/kayit" element={<PublicLayout><MemberRegister /></PublicLayout>} />
            <Route path="/uye/dogrula" element={<PublicLayout><MemberVerify /></PublicLayout>} />
            <Route path="/uye/sifremi-unuttum" element={<PublicLayout><MemberForgotPassword /></PublicLayout>} />
            <Route path="/uye/sifre-sifirla" element={<PublicLayout><MemberResetPassword /></PublicLayout>} />
            <Route path="/profilim" element={<PublicLayout><MemberProfile /></PublicLayout>} />
            <Route path="/odeme-sonuc" element={<PublicLayout><PaymentResult /></PublicLayout>} />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="sliders" element={<SliderManager />} />
              <Route path="projects" element={<ProjectManager />} />
              <Route path="catalogs" element={<CatalogManager />} />
              <Route path="corporate" element={<CorporateEditor />} />
              <Route path="footer" element={<FooterSettings />} />
              <Route path="contact-settings" element={<ContactSettings />} />
              <Route path="seo" element={<SEOSettings />} />
              <Route path="messages" element={<MessagesManager />} />
              <Route path="profile" element={<Profile />} />
              <Route path="users" element={<UserManager />} />
              <Route path="tenants" element={<TenantManager />} />
              <Route path="blog" element={<BlogManager />} />
              <Route path="testimonials" element={<TestimonialManager />} />
              <Route path="team" element={<TeamManager />} />
              <Route path="faq" element={<FAQManager />} />
              <Route path="gallery" element={<GalleryManager />} />
              <Route path="theme" element={<ThemeEditor />} />
              <Route path="menu-settings" element={<MenuSettings />} />
              <Route path="services" element={<ServiceManager />} />
              <Route path="custom-pages" element={<CustomPageManager />} />
              <Route path="legal-pages" element={<LegalPageManager />} />
              <Route path="members" element={<MemberManager />} />
              <Route path="products" element={<ProductManager />} />
              <Route path="payments" element={<PaymentManager />} />
              <Route path="payment-settings" element={<PaymentSettings />} />
              <Route path="newsletter" element={<NewsletterManager />} />
              <Route path="analytics" element={<AnalyticsSettings />} />
            </Route>

            {/* 404 - Catch all */}
            <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
          </Routes>
          </Router>
        </MemberAuthProvider>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
